import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { QrCode, RotateCcw, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ErrorState, LoadingState, Notice, PageHeader, Spinner } from "@/components/common/States";
import { api, errorMessage, fetcher } from "@/lib/api";
import { rupiah } from "@/lib/labels";

const F = ({ id, label, hint, ...p }) => (
  <div className="space-y-2">
    <Label htmlFor={id}>{label}</Label>
    <Input id={id} {...p} />
    {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
  </div>
);

// Gambar QRIS disimpan sebagai JPEG base64 di dokumen reseller; diperkecil di browser
// sebelum dikirim agar muat dalam batas skema (400 ribu karakter).
const kecilkanQris = async (berkas) => {
  if (!berkas.type?.startsWith("image/")) throw new Error("Pilih berkas gambar (JPEG atau PNG).");
  const gambar = await createImageBitmap(berkas, { imageOrientation: "from-image" }).catch(() => null);
  if (!gambar) throw new Error("Gambar tidak bisa dibaca. Coba berkas lain.");
  const skala = Math.min(1, 800 / Math.max(gambar.width, gambar.height));
  const kanvas = document.createElement("canvas");
  kanvas.width = Math.round(gambar.width * skala);
  kanvas.height = Math.round(gambar.height * skala);
  const ctx = kanvas.getContext("2d");
  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(0, 0, kanvas.width, kanvas.height);
  ctx.drawImage(gambar, 0, 0, kanvas.width, kanvas.height);
  gambar.close?.();
  const hasil = kanvas.toDataURL("image/jpeg", 0.85);
  if (hasil.length > 400_000) throw new Error("Gambar masih terlalu besar. Coba gambar QRIS yang lebih sederhana.");
  return hasil;
};

export default function MitraPengaturan() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["mitra", "beranda"], queryFn: () => fetcher("/reseller/beranda") });
  const qris = useQuery({ queryKey: ["mitra", "qris"], queryFn: () => fetcher("/reseller/qris-gambar") });
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [qrisBaru, setQrisBaru] = useState(null);
  const fileRef = useRef(null);

  if (q.isLoading) return <LoadingState rows={6} />;
  if (q.error) return <ErrorState error={q.error} onRetry={q.refetch} />;
  const d = q.data;
  const r = d.reseller;
  const form = f || {
    retail_price_idr: String(r.retail_price_idr ?? ""),
    qris_label: r.qris_label || "",
    qris_nmid: r.qris_nmid || "",
    bank_account: r.bank_account || "",
  };
  const set = (k) => (e) => setF({ ...form, [k]: e.target.value });
  // null = menunggu penghapusan tersimpan; string = gambar baru menunggu tersimpan; undefined = tidak berubah.
  const qrisTampil = qrisBaru === null ? null : (qrisBaru || qris.data?.image);

  const pilihBerkas = async (e) => {
    const berkas = e.target.files?.[0];
    e.target.value = "";
    if (!berkas) return;
    try {
      setQrisBaru(await kecilkanQris(berkas));
      setErr("");
    } catch (ex) {
      setErr(ex.message);
    }
  };

  const simpan = async () => {
    const harga = form.retail_price_idr.trim();
    if (harga && !/^\d+$/.test(harga)) return setErr("Harga jual harus angka rupiah tanpa titik, contoh 3000.");
    if (harga && Number(harga) > d.pengaturan.retail_price_cap_idr) {
      return setErr(`Harga jual tidak boleh melebihi plafon ${rupiah(d.pengaturan.retail_price_cap_idr)} per akun per bulan.`);
    }
    setBusy(true);
    setErr("");
    try {
      const body = {
        retail_price_idr: harga === "" ? null : Number(harga),
        qris_label: form.qris_label,
        qris_nmid: form.qris_nmid,
        bank_account: form.bank_account,
        reason: "Reseller memperbarui pengaturannya di Ruang Mitra",
      };
      if (qrisBaru) {
        body.qris_image = qrisBaru;
      } else if (qris.data?.image && qrisBaru === null) {
        body.remove_qris_image = true;
      }
      const { data } = await api.patch("/reseller/pengaturan", body);
      qc.setQueryData(["mitra", "beranda"], data);
      await qc.invalidateQueries({ queryKey: ["mitra", "qris"] });
      setQrisBaru(undefined);
      setF(null);
      toast.success(`Pengaturan tersimpan. Harga jual ${rupiah(data.reseller.retail_price_idr)} per akun; periode berjalan tetap memakai harga lama di baris tagihannya.`);
    } catch (ex) {
      setErr(errorMessage(ex));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Ruang Mitra SIST" title="Pengaturan"
        description="Atur harga jual per akun (tidak boleh melebihi plafon) dan kanal pembayaran yang dipakai sekolah binaan untuk membayar Anda." />

      <div className="grid lg:grid-cols-2 gap-6">
        <section className="rounded-2xl border bg-card p-6 space-y-5" data-testid="mitra-settings-form">
          <F id="mitra-price" label={`Harga jual per akun per bulan (plafon ${rupiah(d.pengaturan.retail_price_cap_idr)})`}
            inputMode="numeric" value={form.retail_price_idr} onChange={set("retail_price_idr")}
            hint="Berlaku untuk tagihan periode berikutnya. Periode berjalan tetap memakai harga saat tagihan disusun." data-testid="mitra-price-input" />
          <F id="mitra-qris-label" label="Label QRIS" placeholder="Contoh: Strato" value={form.qris_label} onChange={set("qris_label")} data-testid="mitra-qris-label-input" />
          <F id="mitra-qris-nmid" label="NMID QRIS" placeholder="Contoh: ID1026601636883" value={form.qris_nmid} onChange={set("qris_nmid")} data-testid="mitra-qris-nmid-input" />
          <F id="mitra-bank" label="Rekening bank" placeholder="Contoh: BCA 1234567890 a.n. ..." value={form.bank_account} onChange={set("bank_account")} data-testid="mitra-bank-input" />
          {err && <Notice tone="danger" testId="mitra-settings-error">{err}</Notice>}
          <Button onClick={simpan} disabled={busy} data-testid="mitra-settings-submit">
            {busy ? <Spinner /> : <Save className="h-4 w-4 mr-1.5" />} Simpan pengaturan
          </Button>
        </section>

        <section className="rounded-2xl border bg-card p-6 space-y-4" data-testid="mitra-qris-section">
          <div className="flex items-center gap-2">
            <QrCode className="h-5 w-5 text-[#0B3D91]" aria-hidden />
            <h2 className="text-base md:text-lg font-bold">Gambar QRIS</h2>
          </div>
          <p className="text-sm text-muted-foreground">
            Gambar ini yang ditampilkan ke Admin Sekolah pada halaman Langganan. Unggah potongan gambar QRIS statis milik Anda.
          </p>
          <div className="rounded-xl border p-4 grid place-items-center bg-muted/40" data-testid="mitra-qris-preview">
            {qrisTampil ? (
              <img src={qrisTampil} alt="Gambar QRIS reseller" className="max-h-56 rounded-lg" data-testid="mitra-qris-image" />
            ) : (
              <p className="text-sm text-muted-foreground py-8">Belum ada gambar QRIS.</p>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="mitra-qris-upload-button">Unggah gambar</Button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pilihBerkas} data-testid="mitra-qris-file-input" />
            {qris.data?.image && qrisBaru === undefined && (
              <Button variant="ghost" className="text-[#E11D2E]" onClick={() => setQrisBaru(null)} data-testid="mitra-qris-remove-button">
                <RotateCcw className="h-4 w-4 mr-1.5" /> Hapus gambar
              </Button>
            )}
            {qrisBaru === null && (
              <Button variant="ghost" onClick={() => setQrisBaru(undefined)} data-testid="mitra-qris-undo-remove-button">Batalkan penghapusan</Button>
            )}
            {qrisBaru && (
              <Button variant="ghost" onClick={() => setQrisBaru(undefined)} data-testid="mitra-qris-cancel-button">Batalkan gambar baru</Button>
            )}
          </div>
          <p className="text-xs text-muted-foreground">Perubahan gambar ikut tersimpan saat tombol “Simpan pengaturan” ditekan.</p>
        </section>
      </div>
    </div>
  );
}
